/**
 * TeamsView — RBAC teams management settings screen (Phase 4/5).
 *
 * Lists the firm's teams and lets a Firm Admin create one or lock/unlock it.
 * Gated on firm.manage: non-admins see a read-only "no access" state (the
 * backend enforces regardless). The Default team can't be locked.
 */
import React, { useState } from 'react';
import { FiPlus, FiUsers, FiLock, FiUnlock } from 'react-icons/fi';
import { toast } from 'sonner';

import IconWrapper from '../../IconWrapper';
import { Heading } from '../../ui/heading';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../ui/card';
import { useAccess, Capability } from '../../../hooks/useAccess';
import {
  useTeams,
  useCreateTeam,
  useSetTeamActive,
} from '../../../hooks/useTeams';

const TeamsView: React.FC = () => {
  const { can } = useAccess();
  const canManage = can(Capability.FirmManage);
  const { data: teams, isLoading } = useTeams();
  const createTeam = useCreateTeam();
  const setActive = useSetTeamActive();
  const [name, setName] = useState('');

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createTeam.mutateAsync({ name: trimmed });
      setName('');
      toast.success(`Team "${trimmed}" created`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create team');
    }
  };

  const handleToggle = async (teamId: string, isActive: boolean) => {
    try {
      await setActive.mutateAsync({ teamId, isActive: !isActive });
      toast.success(isActive ? 'Team locked' : 'Team unlocked');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update team');
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <Heading level={2}>Teams</Heading>
        <p className="mt-2 text-sm text-muted-foreground">
          You need organization administration access to manage teams.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Heading level={2}>Teams</Heading>
        <p className="mt-1 text-sm text-muted-foreground">
          Teams isolate Monitoring collections and records. Every user belongs
          to at least one team.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create a team</CardTitle>
          <CardDescription>
            New teams start empty — add members and grant roles after creating.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input
            placeholder="Team name (e.g. Banking)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <Button
            onClick={handleCreate}
            loading={createTeam.isPending}
            disabled={!name.trim()}
            leftIcon={<IconWrapper icon={FiPlus} />}
          >
            Create
          </Button>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading teams…</p>
        )}
        {teams?.map((team) => (
          <Card key={team.id}>
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <IconWrapper icon={FiUsers} className="text-muted-foreground" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{team.name}</span>
                    {team.isDefault && <Badge variant="secondary">Default</Badge>}
                    {!team.isActive && <Badge variant="destructive">Locked</Badge>}
                  </div>
                  {team.description && (
                    <p className="text-sm text-muted-foreground">
                      {team.description}
                    </p>
                  )}
                </div>
              </div>
              {!team.isDefault && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggle(team.id, team.isActive)}
                  loading={setActive.isPending}
                  leftIcon={
                    <IconWrapper icon={team.isActive ? FiLock : FiUnlock} />
                  }
                >
                  {team.isActive ? 'Lock' : 'Unlock'}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default TeamsView;
